import { useState, useEffect } from 'react';
import adminApi from '@/utils/adminApi';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Video, Trash2, Mail, Phone, Calendar, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminVideoCallRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await adminApi.get(`/video-call-requests`);
      setRequests(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch video call requests:', error);
      }
      toast.error('Failed to load video call requests');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await adminApi.put(`/video-call-requests/${id}?status=${newStatus}`);
      toast.success('Status updated!');
      fetchRequests();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to update status:', error);
      }
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this video call request?')) return;

    try {
      await adminApi.delete(`/video-call-requests/${id}`);
      toast.success('Video call request deleted!');
      fetchRequests();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-stone-100 text-stone-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'confirmed':
        return 'Confirmed';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'FaceTime':
        return '📱';
      case 'Zoom':
        return '💻';
      case 'WhatsApp':
        return '📲';
      case 'Google Meet':
        return '🎥';
      default:
        return '📹';
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-2xl border border-stone-100">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-stone-200 rounded w-1/3"></div>
          <div className="h-32 bg-stone-100 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-2xl border border-stone-100">
      <div className="flex items-center gap-2 mb-6">
        <Video className="w-6 h-6 text-[#E85D75]" />
        <h2 className="text-2xl font-serif font-medium text-stone-800">Video Call Requests</h2>
        {requests.filter(r => r.status === 'pending').length > 0 && (
          <span className="bg-[#E85D75] text-white text-xs px-2 py-1 rounded-full">
            {requests.filter(r => r.status === 'pending').length} new
          </span>
        )}
      </div>

      <div className="space-y-4">
        {requests.map((request) => (
          <div
            key={request.id}
            data-testid={`video-call-${request.id}`}
            className={`p-6 rounded-xl border ${request.status === 'pending' ? 'border-[#E85D75]/30 bg-rose-50/50' : 'border-stone-100 bg-stone-50'}`}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-stone-800">{request.name}</h3>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(request.status)}`}>
                    {getStatusLabel(request.status)}
                  </span>
                </div>
                <p className="text-[#E85D75] font-medium">
                  Wants to see: {request.kitten_name}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-stone-400">
                  Requested {new Date(request.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>

            {/* Appointment Details */}
            <div className="bg-white rounded-lg p-4 mb-4 border border-stone-100">
              <div className="grid grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#E85D75]" />
                  <div>
                    <p className="text-xs text-stone-500">Date</p>
                    <p className="text-sm font-semibold text-stone-800">{formatDate(request.preferred_date)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#E85D75]" />
                  <div>
                    <p className="text-xs text-stone-500">Time</p>
                    <p className="text-sm font-semibold text-stone-800">{request.preferred_time}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-lg">{getPlatformIcon(request.platform)}</span>
                  <div>
                    <p className="text-xs text-stone-500">Platform</p>
                    <p className="text-sm font-semibold text-stone-800">{request.platform}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="flex items-center gap-2 text-stone-600">
                <Mail className="w-4 h-4" />
                <span className="text-sm">{request.email}</span>
              </div>
              <div className="flex items-center gap-2 text-stone-600">
                <Phone className="w-4 h-4" />
                <span className="text-sm">{request.phone}</span>
              </div>
            </div>

            {request.message && (
              <div className="bg-white p-3 rounded-lg mb-4 border border-stone-100">
                <p className="text-xs text-stone-500 mb-1">Notes:</p>
                <p className="text-sm text-stone-600">{request.message}</p>
              </div>
            )}

            <div className="flex items-center justify-between pt-4 border-t border-stone-200">
              <div className="flex items-center gap-2">
                <span className="text-sm text-stone-600">Status:</span>
                <Select
                  value={request.status}
                  onValueChange={(value) => handleStatusChange(request.id, value)}
                >
                  <SelectTrigger className="w-36 h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    const subject = encodeURIComponent(`Video Call Confirmed - Meet ${request.kitten_name}`);
                    const body = encodeURIComponent(
                      `Hi ${request.name},\n\nGreat news! Your video call to meet ${request.kitten_name} is confirmed!\n\n📅 Date: ${formatDate(request.preferred_date)}\n⏰ Time: ${request.preferred_time}\n📹 Platform: ${request.platform}\n\n${request.platform === 'FaceTime' ? 'We will FaceTime you at the number you provided.' : request.platform === 'Zoom' ? 'Here is your Zoom link: [INSERT ZOOM LINK]' : request.platform === 'WhatsApp' ? 'We will call you on WhatsApp at the number you provided.' : 'Here is your Google Meet link: [INSERT MEET LINK]'}\n\nWe're excited to introduce you to ${request.kitten_name}!\n\nBest regards,\nKittenState`
                    );
                    window.location.href = `mailto:${request.email}?subject=${subject}&body=${body}`;
                  }}
                  className="bg-[#E85D75] text-white hover:bg-[#D64560] rounded-full text-sm"
                >
                  Send Confirmation
                </Button>
                <Button
                  onClick={() => handleDelete(request.id)}
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {requests.length === 0 && (
        <div className="text-center py-12 text-stone-500">
          <Video className="w-16 h-16 mx-auto mb-4 text-stone-200" />
          <p>No video call requests yet.</p>
          <p className="text-sm mt-2">When customers request video calls, they'll appear here.</p>
        </div>
      )}
    </div>
  );
}
